import cloneDeep from 'lodash/cloneDeep';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import TaskService from 'src/services/tasks/taskService';
import { initialState } from './constants';
import { arePrimitiveValuesDiff, arrayMove } from 'src/utils/helpers';
import { createTags, extractAvailableFields, filterByBooleans, filterByStrings } from 'src/utils/tableUtils';
import { FilterTag, FormDataRow, RegionForm, RestrictionFormTableFilter } from './types';
import { RegionTask } from 'src/services/tasks/types';

// **** Functions **** //

/**
 * Convert region object to a table row
 */
const regionToFormDataRow = (region: RegionForm): FormDataRow => ({
  id: region.id,
  name: region.name,
  country: region.country?.name,
  formName: region.restrictionFormName == undefined ? `${region.name} Form` : region.restrictionFormName,
  isActive: region.restrictionFormActive == null ? false : region.restrictionFormActive,
  description: region.restrictionFormDescription,
});

/**
 * Setup the Region/Task Filter Options from the table data.
 */
const setupRestrictionFormTableFilterOptions = (tableData: FormDataRow[]): RestrictionFormTableFilter => ({
  stateRegions: extractAvailableFields(tableData, 'name'),
  applicableCountries: extractAvailableFields(tableData, 'country'),
  formNames: extractAvailableFields(tableData, 'formName'),
  isActiveSelections: ['Yes', 'No'],
});

/**
 * Setup filtered table data using the JobConfig Table filter.
 */
const applyRestrictionFormTableFilterUtil = (
  tableData: FormDataRow[],
  filter: RestrictionFormTableFilter,
): FormDataRow[] => {
  let filteredData = cloneDeep(tableData);
  filteredData = filterByStrings(filteredData, 'name', filter.stateRegions);
  filteredData = filterByStrings(filteredData, 'country', filter.applicableCountries);
  filteredData = filterByStrings(filteredData, 'formName', filter.formNames);
  filteredData = filterByBooleans(
    filteredData,
    'isActive',
    filter.isActiveSelections.map((isActive) => isActive === 'Yes'),
  );
  return filteredData;
};

/**
 * Get the applied filter tabl from the JobConfig Table filter.
 */
const getRestrictionFormTableFilterTags = (filter: RestrictionFormTableFilter): FilterTag[] => [
  ...createTags(filter, 'stateRegions', 'State/Region/Province'),
  ...createTags(filter, 'applicableCountries', 'Country'),
  ...createTags(filter, 'formNames', 'Form Name'),
  ...createTags(filter, 'isActiveSelections', 'Is Active'),
];

/**
 * If a task doesn't have a rank, move it to the top, otherwise sort based on
 * rank ascending.
 */
const sortTasks = (t1: RegionTask, t2: RegionTask) => {
  if (t1.rank == null) {
    return -1;
  } else if (t2.rank == null) {
    return 1;
  } else {
    return t1.rank - t2.rank;
  }
};

/**
 * Change task order.
 */
const changeTaskOrder = (arr: RegionTask[], fromIndex: number, toIndex: number): RegionTask[] => {
  // If moved row don't have a rank, auto move to the first rank
  let updatedRank = 1;
  let updatedIndex = findHighestRankIndex(arr);
  if (arr[toIndex].rank !== null) {
    updatedRank = arr[toIndex].rank;
    updatedIndex = toIndex;
  }
  arrayMove(arr, fromIndex, updatedIndex);
  // Update task rank from start index and rank all the way to the end
  arr[updatedIndex].rank = updatedRank;
  const startIndex = arr.findIndex((val) => val.rank !== null);
  let startRank = 1;
  arr.forEach((item, index) => {
    if (index >= startIndex) {
      item.rank = startRank++;
    }
  });
  // Return
  return arr;
};

/**
 * Corner case when there is no tasks has rank and move select task to
 * bottom.
 */
const findHighestRankIndex = (arr: RegionTask[]): number => {
  for (let i = 0; i < arr.length; i++) {
    if (arr[i].rank === 1) {
      return i - 1;
    }
  }
  return arr.length - 1;
};

// **** Setup API Calls **** //

/**
 * Get regions
 */
export const fetchRegions = createAsyncThunk('regionTasks/fetchRegions', () => {
  return TaskService.fetchRegions();
});

/**
 * Get tasks of a region by region Id
 */
export const fetchRegionTasksById = createAsyncThunk('regionTasks/fetchRegionTasksById', (id: number) => {
  return TaskService.fetchRegionTasksByRegionId(id);
});

/**
 * Get tasks of a region by region id.
 */
export const updateRegionTasks = createAsyncThunk('regionTasks/updateRegionTasks', (tasks: RegionTask[]) => {
  return TaskService.updateRegionTasks(tasks);
});

/**
 * Get tasks of a region by region Id
 */
export const updateRestrictionForm = createAsyncThunk('regionTasks/updateRestrictionForm', (formdata: FormDataRow) => {
  return TaskService.updateRestrictionForm(formdata);
});

// **** Setup **** //

const { reducer, actions } = createSlice({
  name: 'regionTasksConfigPageSlice',
  initialState,
  reducers: {
    setSearchField: (state, action) => {
      state.searchField = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPerPage: (state, action) => {
      state.perPage = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setSelectedRegionTask: (state, action: PayloadAction<RegionTask | null>) => {
      state.selectedRegionTask = action.payload;
    },
    setSelectedRegionTasks: (state, action: PayloadAction<RegionTask[]>) => {
      state.selectedRegionTasks = action.payload;
    },
    setSelectedRegion: (state, action) => {
      state.selectedRegion = action.payload;
    },
    changeTasksOrder: (state, action) => {
      const { old, new: new_ } = action.payload;
      state.selectedRegionTasks = changeTaskOrder(state.selectedRegionTasks, old, new_);
    },
    setIsRestrictionFormTableFilterSheetOpen: (state, action) => {
      state.isRestrictionFormTableFilterOpen = action.payload;
    },
    setRestrictionFormTableFilter: (state, action) => {
      state.restrictionFormTableFilter = action.payload;
    },
    applyRestrictionFormTableFilter: (state) => {
      state.filteredTableData = applyRestrictionFormTableFilterUtil(state.tableData, state.restrictionFormTableFilter);
      state.restrictionFormTableFilterTags = getRestrictionFormTableFilterTags(state.restrictionFormTableFilter);
    },
    resetRestrictionFormTableFilter: (state) => {
      state.restrictionFormTableFilter = {
        stateRegions: [],
        applicableCountries: [],
        formNames: [],
        isActiveSelections: [],
      };
    },
    removeTagAndUpdateFilter: (state, action) => {
      const newFilter = { ...state.restrictionFormTableFilter };
      const filterTag: FilterTag = action.payload;
      const { optionName, value } = filterTag;
      newFilter[optionName] = newFilter[optionName].filter((item) => arePrimitiveValuesDiff(item, value));
      state.restrictionFormTableFilter = newFilter;
      state.restrictionFormTableFilterTags = getRestrictionFormTableFilterTags(newFilter);
      state.filteredTableData = applyRestrictionFormTableFilterUtil(state.tableData, newFilter);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRegions.fulfilled, (state, { payload }) => {
      state.regions = payload;
      const data = payload.map((region: any) => regionToFormDataRow(region));
      state.tableData = data;
      state.filteredTableData = [...data];
      state.restrictionFormTableFilterOptions = setupRestrictionFormTableFilterOptions(data);
    });
    builder.addCase(fetchRegionTasksById.fulfilled, (state, { payload }) => {
      const regionTasks = [...payload];
      state.selectedRegionTasks = regionTasks.sort(sortTasks);
    });
  },
});

// **** Export default **** //

export const {
  setSearchField,
  setCurrentPage,
  setPerPage,
  setTotalPages,
  setSelectedRegion,
  changeTasksOrder,
  setIsRestrictionFormTableFilterSheetOpen,
  setRestrictionFormTableFilter,
  applyRestrictionFormTableFilter,
  resetRestrictionFormTableFilter,
  removeTagAndUpdateFilter,
  setSelectedRegionTask,
  setSelectedRegionTasks,
} = actions;

export default reducer;
